import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  User, Briefcase, IdentificationBadge, Calendar, ChatCircleDots, ChartLine,
  SignOut, Plus, Trash, CheckCircle, Clock, Video, HouseSimple, UsersThree, Sparkle, MapPin, Link as LinkIcon, ArrowRight,
} from '@phosphor-icons/react';
import Header from '@/components/Header';
import PremiumFooter from '@/components/PremiumFooter';
import AvailabilityTab from '@/components/wellness/AvailabilityTab';
import MessagingInbox from '@/components/wellness/MessagingInbox';
import { WELLNESS_CATEGORIES, MODES, LANGUAGES, formatPrice, getCategoryById } from '@/data/wellness';
import { PRO_TOKEN_KEY } from './WellnessProLoginPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MODE_ICON = { online: Video, in_person: UsersThree, home_visit: HouseSimple };

const proAxios = () => axios.create({
  baseURL: BACKEND_URL,
  headers: { Authorization: `Bearer ${localStorage.getItem(PRO_TOKEN_KEY) || ''}` },
});

const TABS = [
  { id: 'overview', label: 'Overview', icon: ChartLine },
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'availability', label: 'Availability', icon: Calendar },
  { id: 'services', label: 'Services', icon: Briefcase },
  { id: 'credentials', label: 'Credentials', icon: IdentificationBadge },
  { id: 'requests', label: 'Requests', icon: ChatCircleDots },
  { id: 'messages', label: 'Messages', icon: ChatCircleDots },
  { id: 'bookings', label: 'Bookings', icon: Calendar },
];

const WellnessProDashboardPage = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [pro, setPro] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPro = useCallback(async () => {
    try {
      const [proRes, metricsRes] = await Promise.all([
        proAxios().get('/api/wellness/pro/me'),
        proAxios().get('/api/wellness/pro/metrics'),
      ]);
      setPro(proRes.data);
      setMetrics(metricsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(PRO_TOKEN_KEY);
        navigate('/wellness/pro/login');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { loadPro(); }, [loadPro]);

  const handleLogout = () => {
    localStorage.removeItem(PRO_TOKEN_KEY);
    toast.success('Logged out');
    navigate('/wellness');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7]">
        <Header user={user} />
        <div className="flex items-center justify-center py-32">
          <div className="w-14 h-14 border-4 border-[#1A4331] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!pro) return null;

  const category = getCategoryById(pro.category_id);

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="pro-dashboard">
      <Header user={user} />

      {/* Header */}
      <section className={`bg-gradient-to-br ${category?.tint || 'from-stone-100 to-stone-200'} px-6 py-10`}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-stone-600 mb-1">Professional Dashboard</p>
            <h1 className="text-3xl md:text-4xl font-semibold text-[#1C1917] font-heading tracking-tight">Welcome, {pro.full_name.split(' ')[0]}</h1>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <span className={`text-[10px] font-bold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full ${pro.status === 'approved' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : pro.status === 'pending' ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="pro-status-badge">
                {pro.status === 'approved' ? '✓ Approved' : pro.status === 'pending' ? '⏳ Pending Review' : 'Rejected'}
              </span>
              {pro.credential_verified && <span className="text-[10px] font-bold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">✓ Credential Verified</span>}
              <span className="text-xs text-stone-500 inline-flex items-center gap-1"><MapPin size={12} weight="fill" /> {pro.city}, {pro.country}</span>
            </div>
          </div>
          <button onClick={handleLogout} className="bg-white border border-stone-200 hover:border-[#1A4331] px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="pro-logout-btn">
            <SignOut size={14} weight="duotone" /> Log out
          </button>
        </div>
      </section>

      {/* Tabs */}
      <section className="border-b border-stone-100 sticky top-0 bg-[#FAFAF7]/95 backdrop-blur z-30 -mt-px">
        <div className="max-w-6xl mx-auto px-6 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-all inline-flex items-center gap-2 ${activeTab === t.id ? 'border-[#1A4331] text-[#1A4331]' : 'border-transparent text-stone-500 hover:text-[#1A4331]'}`}
              data-testid={`pro-tab-${t.id}`}
            >
              <t.icon size={16} weight="duotone" />
              {t.label}
              {t.id === 'requests' && metrics?.pending_requests > 0 && (
                <span className="bg-[#C05C3D] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{metrics.pending_requests}</span>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* Body */}
      <section className="max-w-6xl mx-auto px-6 py-10">
        {pro.status === 'pending' && (
          <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-2xl p-4 mb-6 flex items-start gap-3">
            <Sparkle size={18} weight="fill" className="flex-shrink-0 mt-0.5 text-amber-600" />
            <div className="text-sm leading-relaxed">
              <strong>Your profile is under review.</strong> Complete your services &amp; credentials while you wait — once approved, clients can find and book you.
            </div>
          </div>
        )}

        {activeTab === 'overview' && <OverviewTab pro={pro} metrics={metrics} setActiveTab={setActiveTab} />}
        {activeTab === 'profile' && <ProfileTab pro={pro} onSaved={loadPro} />}
        {activeTab === 'availability' && <AvailabilityTab />}
        {activeTab === 'services' && <ServicesTab pro={pro} />}
        {activeTab === 'credentials' && <CredentialsTab pro={pro} onChanged={loadPro} />}
        {activeTab === 'requests' && <RequestsTab pro={pro} onDone={loadPro} />}
        {activeTab === 'messages' && <MessagingInbox role="pro" />}
        {activeTab === 'bookings' && <BookingsTab />}
      </section>

      <PremiumFooter />
    </div>
  );
};

// ==================== TABS ====================

const OverviewTab = ({ pro, metrics, setActiveTab }) => {
  const stats = [
    { label: 'Upcoming', value: metrics?.upcoming_bookings || 0, color: 'text-[#1A4331]' },
    { label: 'Total Bookings', value: metrics?.total_bookings || 0, color: 'text-stone-700' },
    { label: 'Pending Requests', value: metrics?.pending_requests || 0, color: 'text-[#C05C3D]' },
    { label: 'Avg. Rating', value: pro.avg_rating > 0 ? pro.avg_rating.toFixed(1) : '—', color: 'text-[#F59E0B]' },
  ];
  const earnings = metrics?.earnings_by_currency || [];
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-5 border border-stone-100">
            <p className="text-[10px] uppercase tracking-[0.14em] text-stone-500 font-bold">{s.label}</p>
            <p className={`text-3xl font-semibold font-heading tracking-tight ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Earnings */}
        <div className="bg-white rounded-3xl p-6 border border-stone-100">
          <p className="text-xs uppercase tracking-[0.14em] text-stone-500 font-bold mb-3">Earnings (Mock)</p>
          {earnings.length === 0 ? (
            <p className="text-sm text-stone-500">No completed paid bookings yet.</p>
          ) : (
            <div className="space-y-2">
              {earnings.map((e) => (
                <div key={e.currency} className="flex items-center justify-between border border-stone-100 rounded-2xl p-3">
                  <div>
                    <p className="text-xs text-stone-500">Your payout ({e.currency})</p>
                    <p className="text-xl font-semibold text-[#1A4331] font-heading">{formatPrice(e.payout, e.currency)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-stone-500">Gross</p>
                    <p className="text-sm font-semibold">{formatPrice(e.gross, e.currency)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Checklist */}
        <div className="bg-gradient-to-br from-[#1A4331] to-[#2A5A44] text-white rounded-3xl p-6">
          <p className="text-xs uppercase tracking-[0.14em] text-[#34D399] font-bold mb-3">Complete your profile</p>
          <ul className="space-y-2.5 text-sm">
            {[
              { done: !!pro.headline, label: 'Add a compelling headline', tab: 'profile' },
              { done: !!pro.bio, label: 'Write your bio', tab: 'profile' },
              { done: pro.credential_verified, label: 'Upload &amp; verify credentials', tab: 'credentials' },
              { done: (pro.total_bookings || 0) > 0, label: 'Complete your first booking', tab: 'bookings' },
            ].map((c) => (
              <li key={c.label} className="flex items-center gap-2">
                {c.done ? <CheckCircle size={18} weight="fill" className="text-[#34D399]" /> : <div className="w-4 h-4 rounded-full border-2 border-white/30" />}
                <span className={c.done ? 'text-stone-200 line-through' : 'text-white'} dangerouslySetInnerHTML={{ __html: c.label }} />
                {!c.done && <button onClick={() => setActiveTab(c.tab)} className="ml-auto text-xs text-[#34D399] hover:underline">Do it →</button>}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const ProfileTab = ({ pro, onSaved }) => {
  const [form, setForm] = useState({
    headline: pro.headline || '',
    bio: pro.bio || '',
    languages: pro.languages || [],
    modes: pro.modes || [],
    price_from: pro.price_from || 0,
    service_radius_km: pro.service_radius_km || 10,
    hero_image_url: pro.hero_image_url || '',
    years_experience: pro.years_experience || 0,
    city: pro.city || '',
    state: pro.state || '',
  });
  const [saving, setSaving] = useState(false);

  const setField = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const toggle = (k, v) => setForm((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await proAxios().put('/api/wellness/pro/me', {
        ...form,
        price_from: parseFloat(form.price_from) || 0,
        service_radius_km: parseInt(form.service_radius_km) || 10,
        years_experience: parseInt(form.years_experience) || 0,
      });
      toast.success('Profile updated');
      onSaved();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-white border border-stone-200 focus:border-[#1A4331] focus:ring-4 focus:ring-[#1A4331]/10 rounded-2xl px-4 py-3 outline-none transition-all text-[15px] text-[#1C1917] placeholder:text-stone-400";
  const labelCls = "block text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500 mb-1.5";

  return (
    <div className="bg-white rounded-3xl p-6 md:p-8 border border-stone-100 space-y-5" data-testid="pro-profile-tab">
      <div>
        <label className={labelCls}>Headline</label>
        <input value={form.headline} onChange={setField('headline')} placeholder="e.g., Certified RYT-500 · Vinyasa & Meditation" className={inputCls} data-testid="pro-edit-headline" />
      </div>
      <div>
        <label className={labelCls}>Bio</label>
        <textarea rows={4} value={form.bio} onChange={setField('bio')} placeholder="Share your approach, philosophy, and what clients can expect from you." className={`${inputCls} resize-none`} data-testid="pro-edit-bio" />
      </div>
      <div>
        <label className={labelCls}>Hero Image URL</label>
        <input value={form.hero_image_url} onChange={setField('hero_image_url')} placeholder="https://... (public URL)" className={inputCls} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div><label className={labelCls}>City</label><input value={form.city} onChange={setField('city')} className={inputCls} /></div>
        <div><label className={labelCls}>State</label><input value={form.state} onChange={setField('state')} className={inputCls} /></div>
        <div><label className={labelCls}>Years Experience</label><input type="number" min={0} value={form.years_experience} onChange={setField('years_experience')} className={inputCls} /></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Starting Price ({pro.currency})</label>
          <input type="number" min={0} step="0.01" value={form.price_from} onChange={setField('price_from')} className={inputCls} data-testid="pro-edit-price" />
        </div>
        <div>
          <label className={labelCls}>Service Radius (km)</label>
          <input type="number" min={0} value={form.service_radius_km} onChange={setField('service_radius_km')} className={inputCls} />
        </div>
      </div>
      <div>
        <label className={labelCls}>Modes</label>
        <div className="flex flex-wrap gap-2">
          {MODES.map((m) => (
            <button key={m.id} type="button" onClick={() => toggle('modes', m.id)} className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${form.modes.includes(m.id) ? 'bg-[#1A4331] text-white border-[#1A4331]' : 'bg-white text-stone-700 border-stone-200 hover:border-[#1A4331]'}`}>
              {m.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={labelCls}>Languages</label>
        <div className="flex flex-wrap gap-1.5">
          {LANGUAGES.map((lang) => (
            <button key={lang} type="button" onClick={() => toggle('languages', lang)} className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${form.languages.includes(lang) ? 'bg-[#1A4331] text-white border-[#1A4331]' : 'bg-white text-stone-700 border-stone-200 hover:border-[#1A4331]'}`}>
              {lang}
            </button>
          ))}
        </div>
      </div>
      <button onClick={handleSave} disabled={saving} className="bg-[#1A4331] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#2A5A44] transition-all disabled:opacity-50" data-testid="pro-save-profile-btn">
        {saving ? 'Saving...' : 'Save Profile'}
      </button>
    </div>
  );
};

const ServicesTab = ({ pro }) => {
  const [services, setServices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', duration_min: 45, price: 0, mode: 'online', session_type: 'single' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await proAxios().get('/api/wellness/pro/services');
      setServices(res.data);
    } catch (err) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const handleAdd = async () => {
    if (!form.name || form.price < 0) { toast.error('Name and valid price required'); return; }
    setSaving(true);
    try {
      await proAxios().post('/api/wellness/pro/services', {
        ...form,
        duration_min: parseInt(form.duration_min) || 45,
        price: parseFloat(form.price) || 0,
      });
      toast.success('Service added');
      setShowForm(false);
      setForm({ name: '', description: '', duration_min: 45, price: 0, mode: 'online', session_type: 'single' });
      load();
    } catch (err) { toast.error('Failed to add service'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try { await proAxios().delete(`/api/wellness/pro/services/${id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error('Failed to delete'); }
  };

  const inputCls = "w-full bg-white border border-stone-200 focus:border-[#1A4331] focus:ring-4 focus:ring-[#1A4331]/10 rounded-2xl px-4 py-2.5 outline-none text-sm";

  return (
    <div className="space-y-4" data-testid="pro-services-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold font-heading text-[#1C1917]">Your Services</h2>
          <p className="text-sm text-stone-500 mt-0.5">Individual sessions, packages, or monthly programs.</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="bg-[#1A4331] text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#2A5A44] inline-flex items-center gap-1" data-testid="pro-add-service-btn">
          <Plus size={14} weight="bold" /> Add Service
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl p-5 border border-stone-100 space-y-3" data-testid="pro-service-form">
          <input placeholder="Service Name (e.g., 1:1 Yoga Consultation)" value={form.name} onChange={setField('name')} className={inputCls} data-testid="pro-service-name" />
          <textarea placeholder="Description" rows={2} value={form.description} onChange={setField('description')} className={`${inputCls} resize-none`} />
          <div className="grid grid-cols-3 gap-2">
            <input type="number" placeholder="Duration (min)" value={form.duration_min} onChange={setField('duration_min')} className={inputCls} />
            <input type="number" placeholder={`Price (${pro.currency})`} value={form.price} onChange={setField('price')} className={inputCls} data-testid="pro-service-price" />
            <select value={form.mode} onChange={setField('mode')} className={inputCls}>
              {MODES.filter((m) => (pro.modes || []).includes(m.id)).map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={handleAdd} disabled={saving} className="bg-[#1A4331] text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#2A5A44] disabled:opacity-50" data-testid="pro-service-save">{saving ? 'Saving...' : 'Save'}</button>
            <button onClick={() => setShowForm(false)} className="bg-white border border-stone-200 text-stone-600 px-4 py-2 rounded-full text-sm font-semibold">Cancel</button>
          </div>
        </div>
      )}

      {services.length === 0 ? (
        <div className="bg-white rounded-3xl p-10 border border-stone-100 text-center">
          <Briefcase size={28} weight="duotone" className="text-stone-300 mx-auto mb-2" />
          <p className="text-stone-500 text-sm">No services yet — add one to start offering sessions.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {services.map((s) => {
            const M = MODE_ICON[s.mode];
            return (
              <div key={s.service_id} className="bg-white rounded-2xl p-4 border border-stone-100 flex items-center justify-between" data-testid={`pro-service-${s.service_id}`}>
                <div className="flex-1">
                  <p className="font-semibold text-[#1C1917]">{s.name}</p>
                  {s.description && <p className="text-xs text-stone-500 mt-0.5">{s.description}</p>}
                  <div className="flex items-center gap-3 mt-1 text-xs text-stone-600">
                    <span className="inline-flex items-center gap-1"><Clock size={12} weight="fill" /> {s.duration_min} min</span>
                    {M && <span className="inline-flex items-center gap-1"><M size={12} weight="duotone" /> {MODES.find((x) => x.id === s.mode)?.label}</span>}
                    <span className="font-semibold text-[#1A4331]">{formatPrice(s.price, s.currency)}</span>
                  </div>
                </div>
                <button onClick={() => handleDelete(s.service_id)} className="text-red-500 hover:bg-red-50 p-2 rounded-full"><Trash size={16} weight="duotone" /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CredentialsTab = ({ pro, onChanged }) => {
  const [creds, setCreds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ kind: 'certification', title: '', issuer: '', issue_year: new Date().getFullYear() });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await proAxios().get('/api/wellness/pro/credentials');
      setCreds(res.data);
    } catch (err) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const handleAdd = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    if (!file) { toast.error('Please attach a file (PDF, JPG, PNG)'); return; }
    setSaving(true);
    try {
      // 1. Upload the file
      const fd = new FormData();
      fd.append('file', file);
      const uploadRes = await proAxios().post('/api/wellness/pro/credentials/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const file_id = uploadRes.data.file_id;
      // 2. Create the credential record referencing the file
      await proAxios().post('/api/wellness/pro/credentials', {
        ...form,
        issue_year: parseInt(form.issue_year) || null,
        file_id,
      });
      toast.success('Credential uploaded — pending admin verification');
      setShowForm(false);
      setForm({ kind: 'certification', title: '', issuer: '', issue_year: new Date().getFullYear() });
      setFile(null);
      load();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this credential?')) return;
    try { await proAxios().delete(`/api/wellness/pro/credentials/${id}`); toast.success('Deleted'); load(); onChanged(); }
    catch (err) { toast.error('Failed to delete'); }
  };

  const viewFile = async (fileId) => {
    try {
      const res = await proAxios().get(`/api/wellness/pro/credentials/file/${fileId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error('Failed to open file'); }
  };

  const inputCls = "w-full bg-white border border-stone-200 focus:border-[#1A4331] focus:ring-4 focus:ring-[#1A4331]/10 rounded-2xl px-4 py-2.5 outline-none text-sm";

  return (
    <div className="space-y-4" data-testid="pro-credentials-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold font-heading text-[#1C1917]">Credentials</h2>
          <p className="text-sm text-stone-500 mt-0.5">Upload certificates, licenses &amp; IDs (PDF/JPG/PNG). Only you and Nova admins can see these files.</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="bg-[#1A4331] text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#2A5A44] inline-flex items-center gap-1" data-testid="pro-add-cred-btn">
          <Plus size={14} weight="bold" /> Add Credential
        </button>
      </div>

      <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-2xl p-3 text-xs flex gap-2">
        <IdentificationBadge size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
        <p><strong>Your files are private.</strong> They are stored securely on Nova&apos;s servers and only shared with our verification team. Never posted publicly.</p>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl p-5 border border-stone-100 space-y-3" data-testid="pro-cred-form">
          <div className="grid grid-cols-2 gap-2">
            <select value={form.kind} onChange={setField('kind')} className={inputCls}>
              <option value="certification">Certification</option>
              <option value="license">License</option>
              <option value="education">Education</option>
              <option value="id_document">ID Document</option>
              <option value="other">Other</option>
            </select>
            <input type="number" placeholder="Year issued" value={form.issue_year} onChange={setField('issue_year')} className={inputCls} />
          </div>
          <input placeholder="Title (e.g., RYT-500 Yoga Alliance)" value={form.title} onChange={setField('title')} className={inputCls} data-testid="pro-cred-title" />
          <input placeholder="Issuer / Institution" value={form.issuer} onChange={setField('issuer')} className={inputCls} />
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500 mb-1.5">Upload Document (PDF, JPG, PNG · Max 8 MB) *</label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm text-stone-600 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#1A4331] file:text-white hover:file:bg-[#2A5A44]" data-testid="pro-cred-file-input" />
            {file && <p className="text-xs text-stone-500 mt-1">Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)</p>}
          </div>
          <div className="flex gap-2">
            <button onClick={handleAdd} disabled={saving} className="bg-[#1A4331] text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#2A5A44] disabled:opacity-50" data-testid="pro-cred-save">{saving ? 'Uploading...' : 'Upload'}</button>
            <button onClick={() => { setShowForm(false); setFile(null); }} className="bg-white border border-stone-200 text-stone-600 px-4 py-2 rounded-full text-sm font-semibold">Cancel</button>
          </div>
        </div>
      )}

      {creds.length === 0 ? (
        <div className="bg-white rounded-3xl p-10 border border-stone-100 text-center">
          <IdentificationBadge size={28} weight="duotone" className="text-stone-300 mx-auto mb-2" />
          <p className="text-stone-500 text-sm">No credentials yet.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {creds.map((c) => (
            <div key={c.credential_id} className="bg-white rounded-2xl p-4 border border-stone-100 flex items-center gap-4" data-testid={`pro-cred-${c.credential_id}`}>
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${c.verified ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                {c.verified ? <CheckCircle size={22} weight="fill" /> : <Clock size={22} weight="duotone" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-[#1C1917] truncate">{c.title}</p>
                  <span className={`text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full ${c.verified ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                    {c.verified ? 'Verified' : 'Pending'}
                  </span>
                </div>
                <p className="text-xs text-stone-500 truncate">{c.kind} · {c.issuer || 'No issuer'} {c.issue_year ? `· ${c.issue_year}` : ''}</p>
                {c.file_id ? (
                  <button onClick={() => viewFile(c.file_id)} className="text-xs text-[#1A4331] hover:underline inline-flex items-center gap-1 mt-0.5"><LinkIcon size={11} weight="duotone" /> View document</button>
                ) : c.document_url ? (
                  <a href={c.document_url} target="_blank" rel="noreferrer" className="text-xs text-[#1A4331] hover:underline inline-flex items-center gap-1 mt-0.5"><LinkIcon size={11} weight="duotone" /> View document</a>
                ) : null}
              </div>
              <button onClick={() => handleDelete(c.credential_id)} className="text-red-500 hover:bg-red-50 p-2 rounded-full"><Trash size={16} weight="duotone" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RequestsTab = ({ pro, onDone }) => {
  const [requests, setRequests] = useState([]);
  const [decidingId, setDecidingId] = useState(null);
  const [decision, setDecision] = useState({ scheduled_at: '', service_name: '', price: pro.price_from || 0 });

  const load = useCallback(async () => {
    try {
      const res = await proAxios().get('/api/wellness/pro/consultation-requests');
      setRequests(res.data);
    } catch (err) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleAccept = async (id) => {
    if (!decision.scheduled_at) { toast.error('Please choose a scheduled date/time'); return; }
    try {
      const iso = new Date(decision.scheduled_at).toISOString();
      const res = await proAxios().post(`/api/wellness/pro/consultation-requests/${id}/accept`, {
        scheduled_at: iso,
        service_name: decision.service_name || undefined,
        price: parseFloat(decision.price) || pro.price_from,
        currency: pro.currency,
      });
      toast.success(`Accepted — booking ${res.data.booking_id} created`);
      setDecidingId(null);
      load();
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to accept'); }
  };

  const handleDecline = async (id) => {
    const reason = window.prompt('Reason (optional):') || '';
    try {
      await proAxios().post(`/api/wellness/pro/consultation-requests/${id}/decline`, { reason });
      toast.success('Declined');
      load();
      onDone();
    } catch (err) { toast.error('Failed to decline'); }
  };

  const pending = requests.filter((r) => r.status === 'pending');
  const resolved = requests.filter((r) => r.status !== 'pending');

  return (
    <div className="space-y-6" data-testid="pro-requests-tab">
      <div>
        <h2 className="text-xl font-semibold font-heading text-[#1C1917]">Consultation Requests</h2>
        <p className="text-sm text-stone-500 mt-0.5">Clients who want to talk before booking.</p>
      </div>

      {pending.length === 0 && resolved.length === 0 ? (
        <div className="bg-white rounded-3xl p-10 border border-stone-100 text-center">
          <ChatCircleDots size={28} weight="duotone" className="text-stone-300 mx-auto mb-2" />
          <p className="text-stone-500 text-sm">No consultation requests yet.</p>
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#C05C3D] mb-3">Pending ({pending.length})</p>
              <div className="space-y-3">
                {pending.map((r) => (
                  <div key={r.request_id} className="bg-white rounded-2xl p-5 border border-amber-200" data-testid={`pro-request-${r.request_id}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <p className="font-semibold text-[#1C1917]">{r.customer_name}</p>
                        <p className="text-xs text-stone-500">{r.customer_email} · {r.customer_phone || 'no phone'}</p>
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Pending</span>
                    </div>
                    <p className="text-sm text-stone-700 mb-2"><strong>Goal:</strong> {r.goal}</p>
                    {r.message && <p className="text-sm text-stone-600 mb-2 whitespace-pre-line"><strong>Message:</strong> {r.message}</p>}
                    <div className="flex items-center gap-3 text-xs text-stone-500 mb-3">
                      <span>Mode: <strong className="text-stone-700">{MODES.find((x) => x.id === r.preferred_mode)?.label}</strong></span>
                      {r.preferred_dates.length > 0 && <span>Prefers: <strong className="text-stone-700">{r.preferred_dates.join(' or ')}</strong></span>}
                    </div>

                    {decidingId === r.request_id ? (
                      <div className="bg-stone-50 rounded-2xl p-3 space-y-2">
                        <input type="datetime-local" value={decision.scheduled_at} onChange={(e) => setDecision({ ...decision, scheduled_at: e.target.value })} className="w-full bg-white border border-stone-200 rounded-xl px-3 py-2 text-sm" data-testid={`accept-datetime-${r.request_id}`} />
                        <div className="grid grid-cols-2 gap-2">
                          <input type="text" placeholder="Service name (optional)" value={decision.service_name} onChange={(e) => setDecision({ ...decision, service_name: e.target.value })} className="w-full bg-white border border-stone-200 rounded-xl px-3 py-2 text-sm" />
                          <input type="number" placeholder={`Price (${pro.currency})`} value={decision.price} onChange={(e) => setDecision({ ...decision, price: e.target.value })} className="w-full bg-white border border-stone-200 rounded-xl px-3 py-2 text-sm" />
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => handleAccept(r.request_id)} className="bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-emerald-700" data-testid={`confirm-accept-${r.request_id}`}>Confirm &amp; Create Booking</button>
                          <button onClick={() => setDecidingId(null)} className="bg-white border border-stone-200 text-stone-600 px-4 py-2 rounded-full text-sm font-semibold">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button onClick={() => setDecidingId(r.request_id)} className="bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-emerald-700 inline-flex items-center gap-1" data-testid={`accept-btn-${r.request_id}`}>
                          <CheckCircle size={14} weight="fill" /> Accept
                        </button>
                        <button onClick={() => handleDecline(r.request_id)} className="bg-white border border-red-200 text-red-700 px-4 py-2 rounded-full text-sm font-semibold hover:bg-red-50" data-testid={`decline-btn-${r.request_id}`}>Decline</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {resolved.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-stone-500 mb-3">History</p>
              <div className="space-y-2">
                {resolved.map((r) => (
                  <div key={r.request_id} className="bg-white rounded-2xl p-4 border border-stone-100 flex items-center justify-between opacity-80">
                    <div>
                      <p className="font-semibold text-sm">{r.customer_name} <span className="text-stone-500 font-normal">· {r.goal}</span></p>
                      <p className="text-xs text-stone-500">{r.status} {r.resulting_booking_id ? `→ ${r.resulting_booking_id}` : ''}</p>
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full ${r.status === 'accepted' ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-stone-600'}`}>{r.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const BookingsTab = () => {
  const [bookings, setBookings] = useState([]);
  const load = useCallback(async () => {
    try {
      const res = await proAxios().get('/api/wellness/pro/bookings');
      setBookings(res.data);
    } catch (err) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleComplete = async (id) => {
    if (!window.confirm('Mark this booking as completed? The customer will be able to leave a review.')) return;
    try {
      await proAxios().post(`/api/wellness/pro/bookings/${id}/complete`);
      toast.success('Booking marked completed');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (bookings.length === 0) {
    return (
      <div className="bg-white rounded-3xl p-10 border border-stone-100 text-center" data-testid="pro-bookings-empty">
        <Calendar size={28} weight="duotone" className="text-stone-300 mx-auto mb-2" />
        <p className="text-stone-500 text-sm">No bookings yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="pro-bookings-tab">
      {bookings.map((b) => {
        const when = new Date(b.scheduled_at);
        const M = MODE_ICON[b.mode];
        return (
          <div key={b.booking_id} className="bg-white rounded-2xl p-5 border border-stone-100 flex flex-col md:flex-row md:items-center gap-4" data-testid={`pro-booking-${b.booking_id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <p className="font-semibold text-[#1C1917]">{b.customer_name}</p>
                <span className={`text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full ${b.status === 'confirmed' ? 'bg-emerald-50 text-emerald-700' : b.status === 'completed' ? 'bg-blue-50 text-blue-700' : 'bg-stone-100 text-stone-600'}`}>{b.status}</span>
              </div>
              <p className="text-sm text-stone-500">{b.service_name} · {b.customer_email}</p>
              <div className="flex items-center gap-3 mt-2 text-xs text-stone-600">
                <span className="inline-flex items-center gap-1"><Calendar size={12} weight="fill" /> {when.toLocaleDateString()}</span>
                <span className="inline-flex items-center gap-1"><Clock size={12} weight="fill" /> {when.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>
                {M && <span className="inline-flex items-center gap-1"><M size={12} weight="duotone" /> {MODES.find((x) => x.id === b.mode)?.label}</span>}
              </div>
            </div>
            <div className="text-right flex flex-col items-end gap-2">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-stone-400">Your payout</p>
                <p className="font-semibold text-[#1A4331]">{formatPrice(b.professional_payout, b.currency)}</p>
                <p className="text-xs text-stone-500">of {formatPrice(b.gross_amount, b.currency)} · {b.commission_pct}% commission</p>
              </div>
              {b.status === 'confirmed' && new Date(b.scheduled_at) < new Date() && (
                <button onClick={() => handleComplete(b.booking_id)} className="bg-emerald-600 text-white px-4 py-2 rounded-full text-xs font-bold hover:bg-emerald-700" data-testid={`pro-complete-${b.booking_id}`}>Mark Completed</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default WellnessProDashboardPage;
